import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, EnvelopeOpen, ArrowLeft } from "@phosphor-icons/react";
import BrandLogo from "../components/BrandLogo";

export default function ForgotPasswordPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSubmitted(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not send reset email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center px-4 py-10 sm:py-16">
      <nav className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-3xl bg-white/80 backdrop-blur-md rounded-full px-5 py-2.5 flex justify-between items-center shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-white/40">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <BrandLogo variant="compact" />
        </Link>
        <Link
          to="/login"
          className="text-xs sm:text-sm font-medium text-[#001E44] hover:text-[#007BFF] transition-colors"
          data-testid="nav-login-link"
        >
          Sign in →
        </Link>
      </nav>

      <div className="w-full max-w-md">
        {submitted ? (
          <div
            className="bg-white rounded-3xl border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 sm:p-10 text-center"
            data-testid="forgot-password-success"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#007BFF]/10 text-[#007BFF] mx-auto flex items-center justify-center">
              <EnvelopeOpen size={28} weight="bold" />
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-[#001E44] mt-5">
              Check your inbox
            </h2>
            <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
              If an account exists for <span className="font-semibold text-[#001E44]">{email}</span>, we just sent a reset link.
              It expires in 60 minutes and can only be used once.
            </p>
            <p className="text-xs text-zinc-400 mt-4 leading-relaxed">
              Didn&apos;t get it? Check your spam folder, or wait a minute and try again.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 mt-6 text-sm text-[#007BFF] font-semibold hover:underline"
              data-testid="forgot-back-to-login"
            >
              <ArrowLeft size={14} weight="bold" /> Back to sign in
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 sm:p-10">
            <div className="space-y-2 mb-7">
              <span className="inline-block label-eyebrow text-[#007BFF]">Reset password</span>
              <h1 className="font-display text-3xl font-bold tracking-tight text-[#001E44] leading-tight">
                Forgot your password?
              </h1>
              <p className="text-sm text-zinc-500 leading-relaxed pt-1">
                Enter the email tied to your account and we&apos;ll send a one-time reset link.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-zinc-700">Work email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="you@company.com"
                  data-testid="forgot-email-input"
                  className="rounded-xl bg-zinc-50 border-zinc-200 focus:bg-white focus:border-[#007BFF] focus:ring-[#007BFF]/15 h-11 px-4"
                />
              </div>

              {error && (
                <div
                  className="rounded-xl bg-red-50 border border-red-100 text-red-700 text-xs font-medium px-3 py-2.5"
                  data-testid="forgot-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-[#007BFF] hover:bg-[#0062cc] rounded-full h-12 text-sm font-semibold shadow-sm hover:shadow-md transition-all"
                data-testid="forgot-submit"
              >
                {busy ? "Sending..." : <>Send reset link <ArrowRight size={16} weight="bold" className="ml-2" /></>}
              </Button>

              <p className="text-sm text-zinc-500 text-center pt-1">
                <Link to="/login" className="text-[#007BFF] font-semibold hover:underline" data-testid="link-back-to-login">
                  ← Back to sign in
                </Link>
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
